import { useEffect, useCallback } from "react";
import { pickPrimaryOrg } from '../utils/auth';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  get,
  getCurrentRouteBeforeLogin,
  getLocalStorageToken,
  migrateTokenToCookie,
  post,
  removeCurrentRouteBeforeLogin,
  setApiUserRole,
} from "../utils/api";
import { signInAnonymously, signOut as firebaseSignOut } from "../utils/services/firebase_auth_service";
import { useState } from "react";

// API fetcher for user data
const fetchUserData = async (headers = {}) => {
  const response = await get("/me", headers);
  return response;
};

// Helper function to get browser info
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes("Chrome")) return "Chrome";
  if (userAgent.includes("Firefox")) return "Firefox";
  if (userAgent.includes("Safari")) return "Safari";
  if (userAgent.includes("Edge")) return "Edge";
  return "Unknown";
};

// API function for sending FCM token
const sendFCMTokenToBackend = async ({ fcmToken, headers = {} }) => {
  if (!fcmToken) {
    throw new Error("FCM token is required");
  }

  await post("/token/add", {
    token: fcmToken,
    platform: "web",
    device_info: {
      userAgent: navigator.userAgent,
      browser: getBrowserInfo(),
    },
  }, headers);
};

export const useUser = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentRoute = window.location.pathname;
  // Initialize isLoggedIn based on whether we're attempting authentication
  // This prevents the wildcard redirect during initial page load
  const [isLoggedIn, setIsLoggedIn] = useState(currentRoute !== "/login");

  // Extract token from URL query params
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  // Check if localStorage token exists and needs migration before any API calls
  const localStorageToken = getLocalStorageToken();
  const [migrationComplete, setMigrationComplete] = useState(!localStorageToken);

  // Mutation for token migration - runs before any other API calls
  const tokenMigrationMutation = useMutation({
    mutationFn: migrateTokenToCookie,
    onSuccess: (data) => {
      if (data.success) {
        console.log('Token migrated to cookie successfully');
      } else {
        console.warn('Token migration did not succeed:', data.message);
      }
      setMigrationComplete(true);
    },
    onError: (error) => {
      console.error('Failed to migrate token to cookie:', error);
      setMigrationComplete(true);
    },
  });

  // Trigger migration on mount if localStorage token exists
  useEffect(() => {
    if (localStorageToken && !migrationComplete && !tokenMigrationMutation.isPending) {
      tokenMigrationMutation.mutate(localStorageToken);
    }
  }, []);

  // Query for user data - blocked until migration completes (if needed)
  const {
    data: userData,
    isLoading: isLoadingUserData,
    error,
  } = useQuery({
    queryKey: ['user', 'me', token],
    queryFn: () => {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      return fetchUserData(headers);
    },
    enabled: currentRoute !== '/login' && migrationComplete,
    retry: false,
  });

  // Mutation for sending FCM token
  const fcmTokenMutation = useMutation({
    mutationFn: sendFCMTokenToBackend,
    onError: (error) => {
      console.error('Failed to send FCM token:', error);
    },
  });

  // Mutation for signing in anonymously to Firebase Auth
  const firebaseSignInAnonymouslyMutation = useMutation({
    mutationFn: signInAnonymously,
    onError: (error) => {
      console.error('Failed to sign in anonymously to Firebase Auth:', error);
    },
  });

  const onLoginSuccess = useCallback(async () => {
    // Flip auth state and route to the destination immediately so the user
    // sees the dashboard's full-page loading spinner instead of sitting on
    // the now-stale login form while we hydrate Firebase / `/me`.
    const destination = getCurrentRouteBeforeLogin() || '/';
    removeCurrentRouteBeforeLogin();
    navigate(destination, { replace: true });
    setIsLoggedIn(true);
    try {
      await firebaseSignInAnonymouslyMutation.mutateAsync();
    } catch (error) {
      console.error('Failed to sign in to Firebase Auth:', error);
    }
    try {
      await queryClient.fetchQuery({
        queryKey: ['user', 'me', token],
        queryFn: () => {
          const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
          return fetchUserData(headers);
        },
      });
    } catch (error) {
      console.error('Failed to fetch user data after login:', error);
    }
  }, [queryClient, token, firebaseSignInAnonymouslyMutation, navigate]);
  const logOutMutation = useMutation({
    mutationFn: async () => {
      // Cancel any in-flight queries before tearing down auth state so they
      // don't keep retrying against an unauthenticated session.
      try {
        await queryClient.cancelQueries();
      } catch (error) {
        console.error('Failed to cancel in-flight queries:', error);
      }
      // Sign out from Firebase Auth
      try {
        await firebaseSignOut();
      } catch (error) {
        console.error('Failed to sign out from Firebase Auth:', error);
      }
      // Call logout API to clear cookie on backend
      try {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        await post('/logout', {}, headers);
      } catch (error) {
        console.error('Failed to call logout API:', error);
      }
    },
    onSuccess: () => {
      queryClient.clear();
      setIsLoggedIn(false);
      window.location.href = '/login';
    },
    onError: (error) => {
      console.error('Logout failed:', error);
    },
  });
  const onLogout = useCallback(() => {
    logOutMutation.mutate();
  }, [logOutMutation]);

  // Promote to logged-in once user data arrives. Never demote here — the
  // 401 path below handles logout. Demoting from this effect can race with
  // onLoginSuccess and bounce the user back to /login.
  useEffect(() => {
    if (migrationComplete && !isLoadingUserData && userData) {
      setIsLoggedIn(true);
    }
  }, [userData, isLoadingUserData, migrationComplete]);

  // Handle authentication errors
  useEffect(() => {
    if (error && currentRoute !== "/login") {
      console.error("Authentication error:", error);
      setIsLoggedIn(false);
      // Don't call onLogout here to avoid clearing session unnecessarily
      // The 401 handler in api.js will redirect to login
    }
  }, [error, currentRoute]);
  // Function to send FCM token using mutation
  const sendFCMToken = useCallback((fcmToken) => {
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    fcmTokenMutation.mutate({ fcmToken, headers });
  }, [fcmTokenMutation, token]);

  // Derived state from userData
  const currentUser = userData;
  const isAdmin = userData?.is_quickcare_admin || false;
  const userOrg = pickPrimaryOrg(userData?.organisations);

  // Keep api.js's role cache in sync so it can short-circuit /manage and
  // /admin calls before they hit the network for non-privileged roles.
  useEffect(() => {
    setApiUserRole({ role: userOrg?.access ?? null, isAdmin });
  }, [userOrg?.access, isAdmin]);

  return {
    onLoginSuccess,
    onLogout,
    sendFCMToken,
    currentUser,
    isAdmin,
    userOrg,
    isLoading: !migrationComplete || isLoadingUserData || firebaseSignInAnonymouslyMutation.isPending,
    error,
    isLoggedIn,
    fcmTokenLoading: fcmTokenMutation.isPending,
    fcmTokenError: fcmTokenMutation.error,
    logoutLoading: logOutMutation.isPending,  
  };
};
